// src/App.jsx
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import PropsDashboard from "./PropsDashboard.jsx";
import MSICustomerToday from "./msi/customer/MSICustomerToday";
import MSICustomerDetail from "./msi/customer/MSICustomerDetail";
import MSIProductionOperatorUnavailable from "./components/MSIProductionOperatorUnavailable";

import LoginPage from "./LoginPage";
import ProtectedRoute from "./components/ProtectedRoute";
import MSIOperatorRoute from "./components/MSIOperatorRoute";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfService from "./TermsOfService";
import Contact from "./Contact";
import { useAuth } from "./firebase/AuthContext";
import { EntitlementProvider } from "./entitlements/EntitlementContext";
import PremiumRoute from "./components/PremiumRoute";
import Pricing from "./pages/Pricing";
import Footer from "./Footer";
import BillingSuccess from "./pages/BillingSuccess";
import BillingCancel from "./pages/BillingCancel";

const PRODUCTION_OPERATOR_DEFERRED = process.env.REACT_APP_PWG_PRODUCTION_RELEASE === "1";
const localOperatorComponents = PRODUCTION_OPERATOR_DEFERRED ? {} : {
  dashboard: require("./MSIDashboard").default,
  universe: require("./MSIUniverse").default,
  ticker: require("./MSITickerDetail").default,
  positions: require("./MSIPositions").default,
  closed: require("./MSIClosed").default,
  watchlist: require("./MSIWatchlist").default,
  alerts: require("./MSIAlerts").default,
};

/** Home gate:
 * - While Firebase hydrates: show loading
 * - If logged out after hydration: redirect to /login
 * - If logged in: always show the Sports (Props) dashboard
 */
function HomeGate() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const sport = new URLSearchParams(location.search).get("sport")?.toUpperCase();
  const application = sport === "TEG" || sport === "MSI" ? sport : "PWG";

  if (loading) return <div className="p-4">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  // Logged in → ALWAYS sports (hide any “sports vs. equities” choice)
  return (
    <ProtectedRoute application={application}>
      <PropsDashboard />
    </ProtectedRoute>
  );
}

function PremiumProtected({ children, application }) {
  return (
    <ProtectedRoute application={application}>
      <PremiumRoute application={application}>{children}</PremiumRoute>
    </ProtectedRoute>
  );
}

function GovernedOperatorRoute({ component: Component }) {
  return (
    <PremiumProtected application="MSI">
      <MSIOperatorRoute>
        {PRODUCTION_OPERATOR_DEFERRED ? <MSIProductionOperatorUnavailable /> : <Component />}
      </MSIOperatorRoute>
    </PremiumProtected>
  );
}

const App = () => {
  return (
      <Router>
        <EntitlementProvider>
        {/* Page shell to pin footer to bottom */}
        <div className="min-h-screen flex flex-col bg-white dark:bg-[#0b0c10]">
          <main className="flex-1">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/contact" element={<Contact />} />

              {/* Stripe return URLs */}
              <Route path="/billing/success" element={<BillingSuccess />} />
              <Route path="/billing/cancel" element={<BillingCancel />} />
              <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />

              {/* Auth-aware home (sports only) */}
              <Route path="/" element={<HomeGate />} />

              {/* NEW: TEG (Earnings) dashboard, protected */}
              <Route
                path="/teg"
                element={
                  <PremiumProtected application="TEG"><Navigate to="/?sport=TEG" replace /></PremiumProtected>
                }
              />
              
              {/* MSI Dashboard */}
                <Route
                  path="/msi"
                  element={
                    <PremiumProtected application="MSI"><MSIOperatorRoute><MSICustomerToday /></MSIOperatorRoute></PremiumProtected>
                  }
                />

                <Route
                  path="/msi/structure/:structureId"
                  element={
                    <PremiumProtected application="MSI"><MSIOperatorRoute><MSICustomerDetail /></MSIOperatorRoute></PremiumProtected>
                  }
                />

                <Route
                  path="/msi/operator"
                  element={
                    <GovernedOperatorRoute component={localOperatorComponents.dashboard} />
                  }
                />
                
               {/* MSI Universe */}
                <Route
                  path="/msi/universe"
                  element={
                    <GovernedOperatorRoute component={localOperatorComponents.universe} />
                  }
                />
                
               {/* MSI Ticker Detail */}
                <Route
                  path="/msi/ticker/:ticker"
                  element={
                    <GovernedOperatorRoute component={localOperatorComponents.ticker} />
                  }
                />
                
                {/* MSI Positions */}
                <Route
                  path="/msi/positions"
                  element={
                    <GovernedOperatorRoute component={localOperatorComponents.positions} />
                  }
                />

                {/* MSI Closed */}
                <Route
                  path="/msi/closed"
                  element={
                    <GovernedOperatorRoute component={localOperatorComponents.closed} />
                  }
                />
                
                <Route
                  path="/msi/watchlist"
                  element={
                    <GovernedOperatorRoute component={localOperatorComponents.watchlist} />
                  }
                />
                
                <Route
                  path="/msi/alerts"
                  element={
                    <GovernedOperatorRoute component={localOperatorComponents.alerts} />
                  }
                />


              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
        </div>
        </EntitlementProvider>
      </Router>
  );
};

export default App;
